/**
 * Site Settings API - V2 Generator Management
 */

import { apiGet, apiPost, apiPut } from './client';

/**
 * 'v4' is the current generator and the only one that should appear on new
 * sites. It was missing here while the API returned it, so a V4 site read back
 * as the 'v1' default and the settings UI offered to "switch" it to a legacy
 * generator — which on a V4 site is destructive, not a switch.
 */
export type GeneratorVersion = 'v1' | 'v2' | 'v4';
export type RecipeId = 'bold-modern' | 'classic-elegant' | 'warm-friendly' | 'clean-professional';
export type HeroVariant = 'standard' | 'fullbleed';

export interface RegenerateOptions {
  preserveContent?: boolean;
  preserveImages?: boolean;
}

export interface GeneratorVersionResponse {
  success: boolean;
  message?: string;
  data: {
    websiteId: string;
    businessName: string;
    generatorVersion: GeneratorVersion;
  };
}

export interface RegenerateResponse {
  success: boolean;
  message: string;
  data: {
    websiteId: string;
    businessName: string;
    jobId?: string;
    existingJobId?: string;
    generatorVersion: GeneratorVersion;
    queuePosition?: string;
  };
}

export interface V2StatsResponse {
  success: boolean;
  data: {
    websitesByVersion: Array<{ version: string; count: number }>;
    v2CompletedJobs: number;
    recentV2Jobs: Array<{
      id: string;
      website_id: string;
      status: string;
      queued_at: string;
      completed_at: string | null;
      generation_time_seconds: number | null;
      business_name: string;
    }>;
  };
}

export interface UpdateRecipeResponse {
  success: boolean;
  message: string;
}

export interface GetRecipeResponse {
  success: boolean;
  data: {
    websiteId: string;
    businessName: string;
    recipeId: RecipeId;
    heroVariant: HeroVariant;
    primaryColor: string | null;
    secondaryColor: string | null;
  };
}

/**
 * Get the generator version for a website
 */
export async function getGeneratorVersion(
  websiteId: string
): Promise<GeneratorVersionResponse> {
  return apiGet<GeneratorVersionResponse>(
    `/api/website/${websiteId}/generator-version`
  );
}

/**
 * Set the generator version for a website
 */
export async function setGeneratorVersion(
  websiteId: string,
  version: GeneratorVersion
): Promise<GeneratorVersionResponse> {
  return apiPut<GeneratorVersionResponse>(
    `/api/website/${websiteId}/generator-version`,
    { version }
  );
}

/**
 * Queue a website for regeneration with V2
 */
export async function regenerateWithV2(
  websiteId: string,
  options?: RegenerateOptions
): Promise<RegenerateResponse> {
  return apiPost<RegenerateResponse>(
    `/api/website/${websiteId}/regenerate-v2`,
    options
  );
}

/**
 * Get V2 generation stats
 */
export async function getV2Stats(): Promise<V2StatsResponse> {
  return apiGet<V2StatsResponse>('/api/admin/generator-v2/stats');
}

/**
 * Get recipe settings for a website
 */
export async function getSiteRecipe(
  websiteId: string
): Promise<GetRecipeResponse> {
  return apiGet<GetRecipeResponse>(`/api/website/${websiteId}/recipe`);
}

export interface UpdateRecipeParams {
  recipeId?: RecipeId;
  heroVariant?: HeroVariant;
  primaryColor?: string;
  secondaryColor?: string;
}

/**
 * Update recipe, hero variant, and colors for a website (updates onboarding_data)
 */
export async function updateSiteRecipe(
  websiteId: string,
  params: UpdateRecipeParams
): Promise<UpdateRecipeResponse> {
  return apiPut<UpdateRecipeResponse>(
    `/api/website/${websiteId}/recipe`,
    params
  );
}
